import React, { useState, useEffect } from "react";
import { Menu, LogOut, X, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useAgency } from "../../contexts/AgencyContext";
import { useAskAligned } from "../../contexts/AskAlignedContext";
import WorkspaceSidebar from "./WorkspaceSidebar";
import NotificationBell from "../NotificationBell";
import { PortalSwitcher } from "./PortalSwitcher";
import PilotBanner from "./PilotBanner";
import PilotAcknowledgement from "../../pages/PilotAcknowledgement";
import { api } from "../../lib/api";

// Helper: Get time-of-day greeting
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { agency } = useAgency();
  const { openAskAligned } = useAskAligned();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Pilot Acknowledgement State
  const [checkingAck, setCheckingAck] = useState(true);
  const [requiresAck, setRequiresAck] = useState(false);

  useEffect(() => {
    const checkAcknowledgement = async () => {
      if (!user) {
        setCheckingAck(false);
        return;
      }
      // Skip for caregivers - they use My Leynix portal
      if (user.role === "employee" || user.role === "caregiver") {
        setCheckingAck(false);
        return;
      }
      try {
        const { data } = await api.get("/pilot/acknowledgement/status");
        setRequiresAck(data.requires_acceptance);
      } catch {
        // On error, don't block
        setRequiresAck(false);
      } finally {
        setCheckingAck(false);
      }
    };
    checkAcknowledgement();
  }, [user]);

  // Show loading while checking acknowledgement
  if (checkingAck) {
    return (
      <div className="min-h-screen bg-[#0D1B33] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  // Show Pilot Acknowledgement if required
  if (requiresAck && user) {
    return (
      <PilotAcknowledgement 
        onAccepted={() => setRequiresAck(false)}
        userName={user.name || user.email}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Pilot Banner - Always visible at the top */}
      <PilotBanner />
      
      <div className="flex-1 flex">
        {/* Desktop sidebar */}
        <div className="hidden lg:flex sticky top-0 h-screen">
          <WorkspaceSidebar />
        </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative z-10 h-full flex animate-in slide-in-from-left duration-200">
            <WorkspaceSidebar onNavigate={() => setMobileOpen(false)} />
            <button
              data-testid="close-mobile-nav"
              className="absolute top-4 -right-12 bg-white/90 p-2 rounded-full shadow-lg"
              onClick={() => setMobileOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-14 border-b border-border bg-card/80 backdrop-blur sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6">
          <button
            data-testid="open-mobile-nav"
            className="lg:hidden p-2 rounded-lg hover:bg-secondary transition-colors"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          
          {/* Desktop: Personalized welcome with role */}
          <div className="hidden lg:flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">
              {getGreeting()}, <span className="font-medium text-foreground">{user?.name?.split(" ")[0] || user?.email}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-secondary border border-border capitalize text-muted-foreground">
              {user?.role?.replace(/_/g, " ")}
            </span>
          </div>
          
          {/* Mobile: Agency name */}
          <div className="lg:hidden font-semibold tracking-tight truncate max-w-[140px]" title={agency?.name}>
            {agency?.name || "Leynix"}
          </div>
          
          <div className="flex items-center gap-1">
            {/* Portal Switcher */}
            <PortalSwitcher className="hidden md:flex" />
            
            {/* Ask Leynix Button */}
            <button
              data-testid="ask-aligned-button"
              onClick={openAskAligned}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary hover:bg-secondary/80 border border-border transition-colors text-sm"
              title="Ask Leynix (⌘K)"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Ask</span>
              <kbd className="hidden lg:inline-flex px-1.5 py-0.5 rounded text-[10px] bg-background border border-border ml-1">⌘K</kbd>
            </button>
            <NotificationBell />
            <button
              data-testid="logout-button"
              onClick={logout}
              className="p-2 rounded-lg hover:bg-secondary transition-colors group"
              title="Log out"
            >
              <LogOut className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>
          </div>
        </header>

        <main className="flex-1 min-w-0">{children}</main>
      </div>
      </div>
    </div>
  );
}
